import React from "react"

import { logger } from "./monitoring/logging"
import { safeSessionStorage } from "./safeBrowserStorage"
import { stringifyError } from "./types"

function DummyComponent() {
  return null
}

function getStorageKey(chunkName: string) {
  return `ic.lazy-load-refreshed.${chunkName}`
}

// this fn is from React.lazy
type ImportFn<T> = () => Promise<{ default: React.ComponentType<T> }>

type LazyComponent<T> = ReturnType<typeof React.lazy> & { preload?: ImportFn<T> }

export function lazyWithRetry<T>(importFn: ImportFn<T>, chunkName: string): LazyComponent<T> {
  const Component: LazyComponent<T> = React.lazy(async () => {
    const storageKey = getStorageKey(chunkName)
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(safeSessionStorage.getItem(storageKey) || "false")

    try {
      const component = await importFn()
      safeSessionStorage.setItem(storageKey, "false")

      return component
    } catch (error) {
      logger.warn("lazyWithRetry error", { error: stringifyError(error), pageHasAlreadyBeenForceRefreshed, chunkName })

      if (!pageHasAlreadyBeenForceRefreshed) {
        logger.info("lazyWithRetry reloading page", { chunkName })

        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        safeSessionStorage.setItem(storageKey, "true")
        window.location.reload()

        // this is here to keep TS happy
        return { default: DummyComponent }
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error
    }
  })

  Component.preload = importFn
  return Component
}
